/**
 * What an email, a phone number and a web address LOOK like — for the whole app.
 *
 * WHY THIS IS IN utils/ AND NOT IN A FEATURE FOLDER. The same three questions
 * were being answered in five places, by four different rules:
 *
 *   pages/AI Site/fieldFormats.js     EMAIL_RE, PHONE_RE, URL_RE — Autopilot,
 *                                     the manual builder, the customer drawer
 *   pages/SignUp.js                   /^\S+@\S+\.\S+$/
 *   pages/Home.js                     /^\S+@\S+\.\S+$/
 *   pages/ForgetPassword.js           /^\S+@\S+\.\S+$/
 *   pages/Domain/importFieldRules.js  its own host rule, for the CSV importer
 *
 * They disagreed, and the disagreements were the kind a user meets rather than
 * the kind a developer notices:
 *
 *   münchen.de          the CSV importer accepts it; Autopilot drew a red box
 *   example.com:8080    same
 *   192.168.1.1         same
 *   a@b@c.d             the sign-up form accepted it and every screen after it
 *                       refused it — `\S` matches an @, so the loose rule allows
 *                       a second one
 *
 * A customer whose site is on a port, or has a German domain, could import it
 * and then not type it into the builder. That is one product disagreeing with
 * itself about what a website is.
 *
 * WHERE THE MESSAGES LIVE: not here. A regex is the same everywhere; the
 * sentence shown when it fails belongs to the screen showing it, and
 * `pages/AI Site/fieldFormats.js` owns the FORMATS table that pairs the two.
 */

/**
 * An address: something, an @, something with a dot in it.
 *
 * `[^\s@]` on both sides, NOT `\S` — `\S` matches an @ as well, so the looser
 * rule the auth pages used accepted "a@b@c.d", which every screen downstream
 * then refused. Deliberately permissive otherwise: this rejects what is plainly
 * not an address and leaves the rest to the only authority that can really
 * answer, which is sending mail to it.
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A password we accept: 6+ characters with a letter and a digit — any symbols.
 * An allowlist of symbols refused what phone password managers generate
 * (~ = } : ; " ' > ]) with a message about length, which wasn't the reason.
 * Sign-up and the reset page share it so they can't disagree again.
 */
export const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;

/**
 * Starts with +, a digit, or an opening bracket, then digits and the punctuation
 * people actually type. Six characters minimum — shorter than any real number
 * and long enough to reject a word.
 *
 * Phone numbers are NOT parsed. There is no country context in a bulk batch, so
 * this rejects what is obviously not a number and accepts every real format it
 * cannot adjudicate. A validator that demands +1 (416) 555-0100 rejects most of
 * the world.
 */
export const PHONE_RE = /^[+(\d][\d\s\-().]{5,}$/;

/**
 * A host, as permissively as is still useful.
 *
 * The job is to catch a value of the wrong KIND — a number, an email address, a
 * sentence — not to adjudicate what DNS allows. A rule that rejects a valid
 * address is worse than the bug it was written for: the bug stores a wrong
 * value, the rule stops the work.
 *
 * So all of these pass, and each is a real thing someone runs a site on:
 *   münchen.de              an internationalised domain — \p{L}, not [a-z]
 *   xn--mnchen-3ya.de       the same domain, punycoded
 *   example.com:8080        a non-standard port
 *   192.168.1.1             an address rather than a name
 *   my_site.example.com     an underscore: unusual, but not a typo signal
 *
 * And these do not, which is the whole point:
 *   12345, -1               a number where a host belongs
 *   dax.patel@webis.ca      an email address
 *   a b c, banana           prose, and anything with no dot in it
 */
const IPV4 = /^(\d{1,3}\.){3}\d{1,3}$/;
const NAME = /^(?=.{1,253}$)([\p{L}\p{N}_](([\p{L}\p{N}_-]*)[\p{L}\p{N}_])?\.)+\p{L}{2,}$/u;

/** A host may carry a port; the port is not part of judging the name. */
export const isHost = (value) => {
    const bare = String(value ?? '').trim().replace(/:\d+$/, '');
    return IPV4.test(bare) || NAME.test(bare);
};

/**
 * A web address: a host, with or without a scheme and a path.
 *
 * Deliberately not `new URL()`: that accepts "https://x" (no dot, no TLD) and
 * rejects "example.com" (no scheme), and both answers are backwards for a field
 * where people type the second and never the first.
 */
export const isWebAddress = (value) => {
    const v = String(value ?? '').trim();
    if (!v) return false;
    const afterScheme = v.replace(/^https?:\/\//i, '');
    if (/\s/.test(afterScheme)) return false;
    return isHost(afterScheme.replace(/[/?#].*$/, ''));
};

/**
 * Can the uptime fleet actually check this URL?
 *
 * Backend, 2026-09-24: "We are keeping 80/443, and we agree the honest fix is
 * validating at entry rather than widening the allowlist." They found exactly
 * two monitored URLs carrying an explicit port across 2,288 monitors — both
 * :3306, MySQL — and an HTTP HEAD against MySQL never returns anything
 * meaningful, so both monitors were already broken and nobody had been told.
 *
 * That is the point of checking here: a port we cannot reach does not fail
 * loudly, it produces a monitor that reports nothing useful forever. The address
 * is still a valid address — isWebAddress accepts a port deliberately, because a
 * site can live on one — this asks the narrower question of whether we can
 * MONITOR it.
 */
export const MONITORABLE_PORTS = ['80', '443'];

export const hasMonitorablePort = (value) => {
    const host = String(value ?? '').trim().replace(/^https?:\/\//i, '').replace(/[/?#].*$/, '');
    const m = host.match(/:(\d+)$/);
    return !m || MONITORABLE_PORTS.includes(m[1]);
};

/** A social profile handle — the other half of what a social field accepts. */
export const HANDLE_RE = /^@?[\w.-]{2,}$/;

export default isWebAddress;
